/**
 * E-shop Data Overview Page
 * Similar to the Flask example-app - Shows raw data tables from PostgreSQL
 * with export to Google Sheets functionality
 */

import * as React from 'react';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';
import { useDataSourceStore } from '@store/dataSourceStore';
import { api } from '../services/api';
import { buildURL } from '../config/services';
import {
  ArrowLeft,
  Database,
  RefreshCw,
  AlertCircle,
  Download,
  Calendar,
  FileSpreadsheet,
  Users,
  ShoppingBag,
  Package,
} from 'lucide-react';

interface DataTableOption {
  id: string;
  label: string;
  icon: any;
  description: string;
  endpoint: string;
}

interface EshopDetails {
  id: string;
  name: string;
  type: string;
  purpose?: string;
  config: any;
  isActive: boolean;
}

const EshopDataOverviewPage: React.FC = () => {
  const { eshopId } = useParams<{ eshopId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuthStore();
  const { getSelection } = useDataSourceStore();

  const selection = getSelection();
  const companyId = selection?.companyId;

  const [eshop, setEshop] = useState<EshopDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  
  // Preview state
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activePreview, setActivePreview] = useState<string | null>(null);
  
  // Sync state
  const [syncStatus, setSyncStatus] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{ current: number; total: number } | null>(null);

  // Define available data tables - Daily, Monthly, and Yearly Aggregated Metrics
  const dataTableOptions: DataTableOption[] = [
    {
      id: 'daily-metrics',
      label: 'Daily Metrics',
      icon: ShoppingBag,
      description: 'Daily aggregated metrics: revenue, transactions, new customers',
      endpoint: '/daily-metrics',
    },
    {
      id: 'monthly-metrics',
      label: 'Monthly Metrics',
      icon: Calendar,
      description: 'Monthly aggregated metrics: revenue, transactions, new customers by month',
      endpoint: '/monthly-metrics',
    },
    {
      id: 'yearly-metrics',
      label: 'Yearly Metrics',
      icon: Database,
      description: 'Yearly aggregated metrics: revenue, transactions, new customers by year',
      endpoint: '/yearly-metrics',
    },
  ];

  useEffect(() => {
    fetchEshopDetails();
  }, [eshopId, companyId]);

  const fetchEshopDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!companyId || !eshopId) {
        setError('Missing required parameters');
        setLoading(false);
        return;
      }

      const response = await api.get(`/api/v1/companies/${companyId}`);
      const data = response.data;

      const eshopDataSource = (data.data.dataSources || []).find(
        (ds: any) =>
          (ds.id === eshopId || ds._id === eshopId) && ds.availableModules?.includes('eshop')
      );

      if (!eshopDataSource) {
        throw new Error('E-shop not found');
      }

      setEshop(eshopDataSource);
    } catch (err: any) {
      console.error('Error fetching e-shop details:', err);
      setError(err.message || 'Failed to load e-shop details');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (tableType: string) => {
    try {
      setPreviewLoading(true);
      setActivePreview(tableType);
      setPreviewData(null);

      const response = await fetch(
        buildURL(`/api/v1/eshops/${eshopId}/preview-data?`) +
          new URLSearchParams({
            companyId: companyId || '',
            tableType,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            limit: '10000',
          }),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch preview data');
      }

      const result = await response.json();
      setPreviewData(result.data);
    } catch (err: any) {
      console.error('Error fetching preview:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExportToSheets = async (tableType: string) => {
    if (!confirm(`Export ${tableType} data to Google Sheets?\n\nThis will update the Daily Spreadsheet with the latest data for ${dateRange.startDate} to ${dateRange.endDate}.`)) {
      return;
    }

    try {
      const response = await fetch(
        buildURL(`/api/v1/eshops/${eshopId}/export-to-sheets`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            companyId,
            tableType,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export to Google Sheets');
      }

      const result = await response.json();
      alert(`✅ Successfully exported ${result.data.rowCount} rows to Google Sheets!\n\n📊 Sheet: ${result.data.sheetName}\n🔗 URL: ${result.data.spreadsheetUrl}`);
      
      // Open the sheet in a new tab
      if (result.data.spreadsheetUrl) {
        window.open(result.data.spreadsheetUrl, '_blank');
      }
    } catch (err: any) {
      console.error('Error exporting to sheets:', err);
      alert(`❌ Error: ${err.message}`);
    }
  };

  const handleDownloadCSV = async (tableType: string) => {
    try {
      const response = await fetch(
        buildURL(`/api/v1/eshops/${eshopId}/export-csv?`) +
          new URLSearchParams({
            companyId: companyId || '',
            tableType,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          }),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download CSV');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tableType}-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      console.error('Error downloading CSV:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleSyncToMongoDB = async () => {
    if (!confirm(`Sync data from PostgreSQL to MongoDB?\n\nThis will sync orders from ${dateRange.startDate} to ${dateRange.endDate} into MongoDB.`)) {
      return;
    }

    setSyncing(true);
    setSyncProgress(null);
    setSyncStatus('🔄 Connecting to server...');

    const startTime = Date.now();

    // Use EventSource for Server-Sent Events (SSE)
    // Note: EventSource doesn't support custom headers, so we pass the token as a query param
    const eventSource = new EventSource(
      buildURL(`/api/v1/eshops/${eshopId}/sync-to-mongodb?`) +
      new URLSearchParams({
        companyId: companyId || '',
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        token: accessToken || '', // Pass auth token as query param
      })
    );

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📊 SSE:', data.type, data);

        if (data.type === 'status') {
          setSyncStatus(data.message);
        } else if (data.type === 'total') {
          console.log('🎯 SETTING TOTAL:', data.total);
          setSyncProgress({ current: 0, total: data.total });
          setSyncStatus(data.message);
        } else if (data.type === 'progress') {
          console.log('🚀 PROGRESS UPDATE:', data.current, '/', data.total, '=', data.percentage + '%');
          setSyncProgress({ current: data.current, total: data.total });
          setSyncStatus(data.message);
        } else if (data.type === 'complete') {
          eventSource.close();
          const duration = ((Date.now() - startTime) / 1000).toFixed(1);
          
          setSyncProgress({ current: data.data.ordersCount, total: data.data.ordersCount });
          setSyncStatus(
            `✅ Successfully synced ${data.data.ordersCount.toLocaleString()} orders + ${data.data.itemsCount.toLocaleString()} items!\n` +
            `📊 ${data.data.insertedCount.toLocaleString()} new, ${data.data.updatedCount.toLocaleString()} updated\n` +
            `⏱️ Completed in ${duration}s`
          );
          
          setSyncing(false);
          
          setTimeout(() => {
            setSyncStatus(null);
            setSyncProgress(null);
          }, 8000);
        } else if (data.type === 'error') {
          eventSource.close();
          setSyncStatus(`❌ Error: ${data.error}`);
          setSyncProgress(null);
          setSyncing(false);
          setTimeout(() => setSyncStatus(null), 5000);
        }
      } catch (err) {
        console.error('Error parsing SSE data:', err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('SSE error:', err);
      eventSource.close();
      setSyncStatus('❌ Connection lost');
      setSyncProgress(null);
      setSyncing(false);
      setTimeout(() => setSyncStatus(null), 5000);
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || !eshop) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Error Loading E-shop
          </h3>
          <p className="text-gray-600 dark:text-gray-400">{error || 'E-shop not found'}</p>
          <button
            onClick={() => navigate('/eshops')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Back to E-shops
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/eshops/${eshopId}`)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <Database className="w-8 h-8 text-indigo-600" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {eshop.name} - Data Overview
              </h1>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Export data directly from PostgreSQL to Google Sheets
            </p>
          </div>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Calendar className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Date Range</h2>
          </div>
          <button
            onClick={handleSyncToMongoDB}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync to MongoDB'}
          </button>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={e => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={e => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
        {syncing && !syncProgress && (
          <div className="mt-4 p-6 bg-gradient-to-r from-purple-900/20 to-indigo-900/20 rounded-lg border border-purple-500/30">
            <div className="text-center mb-4">
              <p className="text-3xl font-bold text-purple-400 animate-pulse">⏳ INITIALIZING...</p>
              <p className="text-sm text-gray-400 mt-2">Connecting to PostgreSQL database</p>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-6 overflow-hidden">
              <div className="h-6 bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-500 rounded-full relative overflow-hidden animate-pulse bg-[length:200%_100%] animate-[shimmer_2s_infinite]" style={{ width: '100%' }}>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[slide_1.5s_infinite]"></div>
              </div>
            </div>
          </div>
        )}
        {syncProgress && syncProgress.current < syncProgress.total && (
          <div className="mt-4 p-6 bg-gradient-to-r from-indigo-900/30 to-purple-900/30 rounded-lg border border-indigo-500/40">
            <div className="text-center mb-4">
              <p className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 mb-2">
                {((syncProgress.current / syncProgress.total) * 100).toFixed(1)}%
              </p>
              <p className="text-lg font-semibold text-indigo-300">
                {syncProgress.current.toLocaleString()} / {syncProgress.total.toLocaleString()} orders
              </p>
              <p className="text-sm text-gray-400 mt-1">⚡ Syncing to MongoDB...</p>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-8 overflow-hidden shadow-inner">
              <div 
                className="h-8 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full transition-all duration-200 ease-linear relative overflow-hidden flex items-center justify-center"
                style={{ width: `${Math.max(1, (syncProgress.current / syncProgress.total) * 100)}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[shimmer_1s_infinite]"></div>
                <span className="relative z-10 text-xs font-bold text-white drop-shadow-lg">
                  {syncProgress.current.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        )}
        {syncProgress && syncProgress.current === syncProgress.total && (
          <div className="mt-4 p-6 bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-lg border border-green-500/40">
            <div className="text-center mb-4">
              <p className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400 mb-2">
                ✅ 100%
              </p>
              <p className="text-lg font-semibold text-green-300">
                {syncProgress.current.toLocaleString()} / {syncProgress.total.toLocaleString()} orders
              </p>
              <p className="text-sm text-gray-400 mt-1">🎉 Sync Complete!</p>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-8 overflow-hidden shadow-inner">
              <div 
                className="h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500 ease-out animate-pulse"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        )}
        {syncStatus && (
          <div className={`mt-4 p-3 rounded-lg whitespace-pre-line ${syncStatus.startsWith('✅') ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200' : syncStatus.startsWith('🔄') ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200' : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200'}`}>
            {syncStatus}
          </div>
        )}
      </div>

      {/* Data Tables Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dataTableOptions.map(option => (
          <div
            key={option.id}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-xl transition-all"
          >
            {/* Icon and Title */}
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                <option.icon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {option.label}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {option.description}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <button
                onClick={() => handlePreview(option.id)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Preview Data
              </button>
              <button
                onClick={() => handleExportToSheets(option.id)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Export to Google Sheets
              </button>
              <button
                onClick={() => handleDownloadCSV(option.id)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download CSV
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Preview Section */}
      {activePreview && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Preview: {dataTableOptions.find(o => o.id === activePreview)?.label}
            </h2>
            <button
              onClick={() => setActivePreview(null)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ✕ Close
            </button>
          </div>

          {previewLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : previewData ? (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Showing {previewData.rows?.length || 0} rows ({previewData.total || 0} days with data in selected range)
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-gray-700">
                      {previewData.headers?.map((header: string, idx: number) => (
                        <th
                          key={idx}
                          className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-medium text-gray-900 dark:text-white"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.rows?.map((row: any, rowIdx: number) => (
                      <tr
                        key={rowIdx}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        {previewData.headers?.map((header: string, colIdx: number) => (
                          <td
                            key={colIdx}
                            className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-300"
                          >
                            {row[header] !== null && row[header] !== undefined
                              ? String(row[header])
                              : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              No preview data available
            </p>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex gap-3">
          <Database className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
              About Data Sync & Export
            </h4>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Sync to MongoDB:</strong> Queries PostgreSQL datasource and stores orders + items in MongoDB (multi-tenant collection)
              <br />
              <strong>Daily Metrics:</strong> Aggregated by day - Revenue, Transactions, New Customers, Avg Order Value
              <br />
              <strong>Monthly Metrics:</strong> Aggregated by month (YYYY-MM format) - Same metrics as daily but grouped by month
              <br />
              <strong>Yearly Metrics:</strong> Aggregated by year (YYYY format) - Same metrics but grouped by year
              <br />
              <strong>Google Sheets:</strong> Exports to sheet named "Daily/Monthly/Yearly RAW - {'{current_date}'}"
              <br />
              <strong>Updates:</strong> Only appends new records, doesn't clear existing data
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EshopDataOverviewPage;

